<template>
  <div
    :class="{ fullscreen: fullscreen }"
    :style="{ width: containerWidth }"
    class="tinymce-container"
  >
    <textarea :id="tinymceId" autocomplete="off" class="tinymce-textarea" />
    <div v-if="isImage" class="editor-custom-btn-container">
      <editorImage class="editor-upload-btn" color="#00A551" @successCBK="imageSuccessCBK" />
    </div>
  </div>
</template>

<script>
/**
 * docs:
 * https://panjiachen.github.io/vue-element-admin-site/feature/component/rich-editor.html#tinymce
 */
import editorImage from './components/EditorImage';
import plugins from './plugins';
import toolbar from './toolbar';
import load from './dynamicLoadScript';
import { OSSMessageBoardUpload } from '@/utils/ali_oos_util';

// why use this cdn, detail see https://github.com/PanJiaChen/tinymce-all-in-one
// const tinymceCDN = 'https://cdn.jsdelivr.net/npm/tinymce-all-in-one@4.9.3/tinymce.min.js';
const tinymceCDN =
  'https://anjunggl-1313947203.cos.ap-guangzhou.myqcloud.com/app/static/js/tinymce-all-in-one/tinymce.min.js';

export default {
  name: 'Tinymce',
  components: { editorImage },
  props: {
    id: {
      type: String,
      default: function() {
        return 'vue-tinymce-' + +new Date() + ((Math.random() * 1000).toFixed(0) + '');
      }
    },
    value: {
      type: String,
      default: ''
    },
    toolbar: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    menubar: {
      type: String,
      default: 'file edit insert view format table'
    },
    height: {
      type: [Number, String],
      required: false,
      default: 360
    },
    width: {
      type: [Number, String],
      required: false,
      default: 'auto'
    },
    readonlyVal: {
      type: Number,
      default: 0
    },
    msgPlugin: {
      type: Array,
      default: () => {
        return [];
      }
    },
    placeholder: {
      type: String,
      default: ''
    },
    isImage: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data() {
    return {
      hasChange: false,
      hasInit: false,
      tinymceId: this.id,
      fullscreen: false,
      languageTypeList: {
        en: 'en',
        zh: 'zh-Hans',
        pu: 'pt-BR'
      }
    };
  },
  computed: {
    containerWidth() {
      const width = this.width;
      if (/^[\d]+(\.[\d]+)?$/.test(width)) {
        // matches `100`, `'100'`
        return `${width}px`;
      }
      return width;
    }
  },
  watch: {
    value(val) {
      // console.log('value(val)', val);
      // debugger
      if (!this.hasChange && this.hasInit) {
        // if (this.hasInit) {
        this.$nextTick(() => window.tinymce.get(this.tinymceId).setContent(val || ''));
      }
      // 此处暂时采用硬编码
      // if (val === '清空') {
      //   this.$nextTick(() =>
      //     window.tinymce.get(this.tinymceId).setContent(''));
      // }
      // 目前发现val的值只有空
      if (val === '') {
        this.$nextTick(() => window.tinymce.get(this.tinymceId).setContent(''));
      }
    }
  },
  created() {
    // console.log('value(val)微工单初始状态下的内容', this.value);
  },
  mounted() {
    this.init();
    this.bus.$on('selectVar', (data) => {
      const tinymce = window.tinymce.get(this.tinymceId);
      tinymce.execCommand('mceInsertContent', false, '{' + data + '}');
    });
  },
  activated() {
    if (window.tinymce) {
      this.initTinymce();
    }
  },
  deactivated() {
    this.destroyTinymce();
  },
  destroyed() {
    this.destroyTinymce();
  },
  methods: {
    formatHtmlParagraphs(originalHtml) {
      // 正则1：匹配所有<div 带任意属性> → 替换为<p>（移除所有属性/样式）
      const replaceOpenTag = originalHtml.replace(/<div[^>]*>/gi, '<p>');
      // 正则2：匹配所有</div> → 替换为</p>
      const finalHtml = replaceOpenTag.replace(/<\/div>/gi, '</p>');
      // 正则1：匹配所有<span 带任意属性> → 替换为<p>（移除所有属性/样式）
      const replaceOpenTag1 = finalHtml.replace(/<span[^>]*>/gi, '<p>');
      // 正则2：匹配所有</span> → 替换为</p>
      const finalHtml1 = replaceOpenTag1.replace(/<\/span>/gi, '</p>');
      // 1. 提取<p>标签内的纯文本内容（去除外层<p>和</p>）
      const innerContent = finalHtml1.replace(/^<p>/, '').replace(/<\/p>$/, '');
      // 2. 按<br /><br />拆分段落，同时清理可能的&nbsp;空格（保留语义内空格）
      const paragraphs = innerContent
        .split(/<br\s*\/?><br\s*\/?>/) // 匹配<br>或<br/>的双重换行分隔符
        .map((para) => para.replace(/&nbsp;/g, ' ').trim()) // 替换&nbsp;为普通空格，去除段落首尾空字符
        .filter((para) => para); // 过滤空段落（避免拆分后出现空内容）
      // 3. 重组为“<p>段落</p><p>&nbsp;</p>”格式，最后一段后不额外加空行
      let formattedHtml = '';
      paragraphs.forEach((para, index) => {
        formattedHtml += `<p style="word-wrap: break-word;">${para}</p>`;
        // 不是最后一段时，添加空行标签
        if (index !== paragraphs.length - 1) {
          formattedHtml += '<p style="word-wrap: break-word;">&nbsp;</p>';
        }
      });
      return formattedHtml;
    },
    init() {
      // dynamic load tinymce from cdn
      load(tinymceCDN, (err) => {
        if (err) {
          this.$message.error(err.message);
          return;
        }
        this.initTinymce();
      });
    },
    initTinymce() {
      const _this = this;
      let placeholder = null;
      if (_this.placeholder) {
        placeholder = '<p class="placeholderaaa" style="color: #aaa;">' + _this.placeholder + '</p>';
      }
      const language = sessionStorage.getItem('language') === 'zh' ? '/zh-Hans.js' : '/pt_BR.js';
      window.tinymce.init({
        selector: `#${this.tinymceId}`,
        // language: this.languageTypeList[language],
        language_url: language,
        height: this.height,
        body_class: 'panel-body ',
        object_resizing: false,
        toolbar: this.toolbar.length > 0 ? this.toolbar : toolbar,
        menubar: this.menubar,
        plugins: this.msgPlugin.length > 0 ? this.msgPlugin : plugins,
        end_container_on_empty_block: true,
        powerpaste_word_import: 'clean',
        code_dialog_height: 450,
        code_dialog_width: 1000,
        advlist_bullet_styles: 'square',
        advlist_number_styles: 'default',
        imagetools_cors_hosts: ['www.tinymce.com', 'codepen.io'],
        default_link_target: '_blank',
        link_title: false,
        nonbreaking_force_tab: true, // inserting nonbreaking space &nbsp; need Nonbreaking Space Plugin
        paste_data_images: true, // 上传
        images_file_types: 'jpeg,jpg,png,gif,bmp,webp',
        branding: false,
        contextmenu_never_use_native: false,
        init_instance_callback: (editor) => {
          if (_this.value) {
            editor.setContent(_this.value);
          }
          _this.hasInit = true;
          editor.on('NodeChange Change KeyUp SetContent', () => {
            this.hasChange = true;
            let content = editor.getContent();
            if (editor.getContent() === placeholder) {
              content = '';
            }
            let formattedHtml = '';
            if (content) {
              // 2. 调用方法转换
              formattedHtml = this.formatHtmlParagraphs(content);
            }
            this.$emit('input', formattedHtml);
          });
        },
        setup(editor) {
          // 输入框增加 "请输入内容..." 占位符
          if (_this.placeholder) {
            editor.on('init', () => {
              if (editor.getContent().length === 0) {
                editor.setContent(placeholder);
              }
            });

            editor.on('focus', () => {
              if (editor.getContent().length > 0) {
                if (editor.getContent() === placeholder) {
                  editor.setContent('');
                }
              }
            });

            editor.on('blur', () => {
              if (editor.getContent().length === 0) {
                editor.setContent(placeholder);
              }
            });
          }
          editor.on('FullscreenStateChanged', (e) => {
            _this.fullscreen = e.state;
          });
        },
        // it will try to keep these URLs intact
        // https://www.tiny.cloud/docs-3x/reference/configuration/Configuration3x@convert_urls/
        // https://stackoverflow.com/questions/5196205/disable-tinymce-absolute-to-relative-url-conversions
        convert_urls: false,
        // 阿里云上传图片
        images_upload_handler: (blobInfo, success, failure) => {
          const file = blobInfo.blob();

          const reader = new FileReader();

          reader.readAsArrayBuffer(file);

          reader.onload = (e) => {
            let blob = null;
            if (typeof e.target.result === 'object') {
              blob = new Blob([e.target.result]);
            } else {
              blob = e.target.result;
            }
            OSSMessageBoardUpload(blob, 'workOrder')
              .then((res) => {
                success(res.url);
              })
              .catch((err) => {
                failure(err);
                console.log(err);
              });
          };
        },

        content_style: 'img {max-width:500px; height: 100%}',

        readonly: this.readonlyVal
      });
    },
    destroyTinymce() {
      const tinymce = window.tinymce.get(this.tinymceId);
      if (this.fullscreen) {
        tinymce.execCommand('mceFullScreen');
      }

      if (tinymce) {
        tinymce.destroy();
      }
    },
    setContent(value) {
      window.tinymce.get(this.tinymceId).setContent(value);
    },
    getContent() {
      window.tinymce.get(this.tinymceId).getContent();
    },
    imageSuccessCBK(arr) {
      arr.forEach((v) =>
        window.tinymce
          .get(this.tinymceId)
          .insertContent(`<img alt="" style="width:150px;max-height:150px;" src="${v.url}" >`)
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.tinymce-container {
  position: relative;
  line-height: normal;
}

.tinymce-container {
  ::v-deep {
    .mce-fullscreen {
      z-index: 10000;
    }
  }
}

.tinymce-textarea {
  visibility: hidden;
  z-index: -1;
}

.editor-custom-btn-container {
  position: absolute;
  right: 4px;
  top: 4px;
  /*z-index: 2005;*/
}

.fullscreen .editor-custom-btn-container {
  z-index: 10000;
  position: fixed;
}

.editor-upload-btn {
  display: inline-block;
}
</style>
